import { Link } from 'react-router-dom';
import { Button, Card, EmptyState, alertCategoryLabel } from '@aigovops/design-system';
import type { AlertCategory, AlertSubscription } from '../lib/api';

/**
 * Positive empty state for AlertsInbox (UC-4 R1/R2 design-rationale
 * §"Empty state + preferences UX decisions") — green check, "You're all
 * caught up." tone, no primary CTA (nothing to act on). Reassurance
 * card answers "am I actually covered?" by showing each category's
 * subscription state, pulled from the caller's own preferences.
 */

const ALL_CATEGORIES: AlertCategory[] = [
  'legislation',
  'litigation',
  'framework',
  'breach',
  'cert',
];

export function AlertsEmpty({
  prefix,
  subscription,
  aggregate = false,
  workspaceCount = 0,
}: {
  prefix: string;
  subscription: AlertSubscription | null;
  /** Rendered inside the consultant aggregate inbox (PR-P3-4). */
  aggregate?: boolean;
  workspaceCount?: number;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <EmptyState
        glyph="✓"
        title={aggregate ? `All ${workspaceCount} clients are caught up.` : "You're all caught up."}
        description="No open alerts right now. New legislation, litigation, breach intel, framework revisions, and cert deadlines land here as they're found."
        action={
          <>
            <Link to={`${prefix}/alerts/preferences`}>
              <Button variant="secondary">Configure alert preferences →</Button>
            </Link>
            <Link
              to={
                aggregate
                  ? `${prefix}/alerts?view=aggregate&status=all&window=30d`
                  : `${prefix}/alerts?status=all&window=30d`
              }
            >
              <Button variant="ghost">See past 30 days →</Button>
            </Link>
          </>
        }
      />
      {/* Subscription state is per-workspace, so in aggregate mode this
          card would answer "am I covered?" for one client while the
          headline above spoke for all of them. Preferences stay one
          click away either way. */}
      {aggregate ? null : (
      <Card title="What we watch on your behalf">
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {ALL_CATEGORIES.map((c) => {
            const on = subscription ? subscription.categories.includes(c) : true;
            return (
              <li
                key={c}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px var(--space-3)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-input)',
                }}
              >
                <span style={{ color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                  {alertCategoryLabel(c)}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-3xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: on ? 'var(--success-fg)' : 'var(--text-muted)',
                    fontWeight: 600,
                  }}
                >
                  {on ? 'On' : 'Off'}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>
      )}
    </div>
  );
}
